/**
 * Gift with purchase (GWP) for the TBW theme.
 *
 * Source of truth: bundle-disocount-app/theme/assets/gwp-cart.js (with tests).
 * Copy this file unchanged into the theme's assets/ folder.
 *
 * The condition lives in the app-owned gwp_config metaobject, which the
 * gwp-discount Function also reads, so adding the gift here and pricing it at
 * checkout can't disagree. The config reaches this module through
 * snippets/gwp-config.liquid as <script id="gwp-config" type="application/json">.
 *
 * Gift lines are marked with the line property _gwp = "1". This module only
 * ever adds, or removes, lines that carry _gwp.
 */

export const GWP_PROPERTY = '_gwp';
export const GWP_PROPERTY_VALUE = '1';
const SOURCE = 'gwp-cart';
const CODES_STORAGE_KEY = 'gwp:codes';

// ---------------------------------------------------------------------------
// Pure logic (no DOM, no network): covered by theme/tests
// ---------------------------------------------------------------------------

/**
 * @typedef {object} GwpConfig
 * @property {boolean} active
 * @property {number | string | null} threshold - In the store's currency
 * @property {number | string | null} giftVariantId - Numeric id or ProductVariant GID
 * @property {string | null} code - The GWP discount code
 * @property {string[]} [exclusiveCodes] - Our other codes that must not stack with it (e.g. TBW-POPUP16)
 */

/**
 * Whether a cart.js item carries the _gwp property.
 * @param {{ properties?: Record<string, unknown> | null }} item
 */
export function isGiftLine(item) {
  return item.properties != null && item.properties[GWP_PROPERTY] != null;
}

/**
 * Subtotal in cents (cart currency) of every line that does NOT carry _gwp,
 * before discounts. Mirrors the Function's sum of cost.subtotalAmount.
 * @param {{ items: Array<{ original_line_price: number, properties?: Record<string, unknown> | null }> }} cart
 */
export function subtotalExcludingGiftCents(cart) {
  return cart.items.filter((item) => !isGiftLine(item)).reduce((sum, item) => sum + item.original_line_price, 0);
}

/** @param {{ discount_codes?: Array<{ code: string }> }} cart */
export function cartCodes(cart) {
  return (cart.discount_codes || []).map((discount) => discount.code);
}

/**
 * Discount codes are case-insensitive.
 * @param {string[]} codes
 * @param {string | null | undefined} code
 */
export function includesCode(codes, code) {
  if (!code) return false;
  const wanted = code.trim().toUpperCase();
  return codes.some((c) => c.trim().toUpperCase() === wanted);
}

/** @param {number | string | null | undefined} id */
export function variantNumericId(id) {
  const numeric = Number(String(id ?? '').split('/').pop());
  return Number.isInteger(numeric) && numeric > 0 ? numeric : null;
}

/**
 * The one GWP condition: offer active, subtotal excluding _gwp lines >= threshold,
 * and the GWP code in cart.discount_codes. The code does NOT need to be
 * applicable: before the gift line exists the Function has nothing to discount,
 * so Shopify reports the code as not applicable.
 *
 * @param {Parameters<typeof subtotalExcludingGiftCents>[0] & Parameters<typeof cartCodes>[0]} cart
 * @param {GwpConfig | null} config
 * @param {number} [rate] - Presentment currency rate (Shopify.currency.rate)
 */
export function isEligible(cart, config, rate = 1) {
  if (!config || config.active !== true || !config.code || variantNumericId(config.giftVariantId) === null) {
    return false;
  }
  const threshold = Number(config.threshold);
  if (config.threshold === null || config.threshold === '' || !Number.isFinite(threshold)) return false;

  return (
    includesCode(cartCodes(cart), config.code) &&
    subtotalExcludingGiftCents(cart) >= Math.round(threshold * (Number(rate) || 1) * 100)
  );
}

/**
 * What to do with the gift line.
 * @returns {{ type: 'add', variantId: number }
 *   | { type: 'remove', lineKeys: string[] }
 *   | { type: 'none' }}
 */
export function decideGiftAction(cart, config, rate = 1) {
  const giftLines = cart.items.filter(isGiftLine);
  const eligible = isEligible(cart, config, rate);

  if (eligible && giftLines.length === 0) {
    return { type: 'add', variantId: /** @type {number} */ (variantNumericId(config?.giftVariantId)) };
  }
  if (!eligible && giftLines.length > 0) {
    return { type: 'remove', lineKeys: giftLines.map((item) => item.key) };
  }
  return { type: 'none' };
}

/**
 * Last code entered wins: when the cart holds more than one of our codes, keep
 * only the most recently entered one. "Most recent" is the code that wasn't in
 * the cart last time we looked; if that's ambiguous, the last one in the
 * cart's list (the theme appends new codes). Codes that aren't ours are kept.
 *
 * @param {Parameters<typeof cartCodes>[0]} cart
 * @param {string[]} ourCodes
 * @param {string[]} [previousCodes] - cart.discount_codes the last time we checked
 * @returns {{ winner: string, discount: string } | null} discount is the value for /cart/update.js
 */
export function resolveCodeConflict(cart, ourCodes, previousCodes = []) {
  const codes = cartCodes(cart);
  const ours = codes.filter((code) => includesCode(ourCodes, code));
  if (ours.length <= 1) return null;

  const fresh = ours.filter((code) => !includesCode(previousCodes, code));
  const candidates = fresh.length > 0 ? fresh : ours;
  const winner = candidates[candidates.length - 1];
  const others = codes.filter((code) => !includesCode(ourCodes, code));

  return { winner, discount: [...others, winner].join(',') };
}

/**
 * All of our codes: the GWP code plus the codes it must not stack with.
 * @param {GwpConfig | null} config
 */
export function ourCodes(config) {
  if (!config) return [];
  return [config.code, ...(config.exclusiveCodes || [])].filter((code) => typeof code === 'string' && code !== '');
}

// ---------------------------------------------------------------------------
// Browser glue
// ---------------------------------------------------------------------------

const CART_MUTATION_PATH = /\/cart\/(add|change|update)(\.js)?$/;

/** @param {string} url */
function isCartMutationUrl(url) {
  try {
    return CART_MUTATION_PATH.test(new URL(url, window.location.href).pathname);
  } catch {
    return false;
  }
}

/** @param {RequestInfo | URL} input */
function requestUrl(input) {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.href;
  return input.url;
}

function readConfigFromPage() {
  const element = document.getElementById('gwp-config');
  if (!element) return null;
  try {
    return /** @type {GwpConfig} */ (JSON.parse(element.textContent || 'null'));
  } catch {
    return null;
  }
}

function loadPreviousCodes() {
  try {
    return JSON.parse(sessionStorage.getItem(CODES_STORAGE_KEY) || '[]');
  } catch {
    return [];
  }
}

/** @param {string[]} codes */
function savePreviousCodes(codes) {
  try {
    sessionStorage.setItem(CODES_STORAGE_KEY, JSON.stringify(codes));
  } catch {
    // Private mode or storage blocked: last-code-wins falls back to list order
  }
}

export function init() {
  const config = readConfigFromPage();
  if (!config) return;

  const nativeFetch = window.fetch.bind(window);
  const root = window.Shopify?.routes?.root || '/';
  const codes = ourCodes(config);

  let running = false;
  let rerun = false;
  // A failed add (out of stock, variant missing) isn't retried on this page
  let addFailed = false;
  let previousCodes = loadPreviousCodes();

  /** @param {string} path @param {object} body */
  const post = (path, body) =>
    nativeFetch(`${root}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });

  const getCart = async () => (await nativeFetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })).json();

  async function reconcile() {
    let cart = await getCart();
    let changed = false;

    const conflict = resolveCodeConflict(cart, codes, previousCodes);
    if (conflict) {
      const response = await post('cart/update.js', { discount: conflict.discount });
      if (response.ok) {
        cart = await response.json();
        changed = true;
      }
    }
    previousCodes = cartCodes(cart);
    savePreviousCodes(previousCodes);

    const action = decideGiftAction(cart, config, Number(window.Shopify?.currency?.rate) || 1);
    if (action.type === 'add' && !addFailed) {
      const response = await post('cart/add.js', {
        items: [{ id: action.variantId, quantity: 1, properties: { [GWP_PROPERTY]: GWP_PROPERTY_VALUE } }],
      });
      if (response.ok) changed = true;
      else addFailed = true;
    } else if (action.type === 'remove') {
      for (const key of action.lineKeys) {
        const response = await post('cart/change.js', { id: key, quantity: 0 });
        if (response.ok) changed = true;
      }
    }

    if (changed) {
      const fresh = await getCart();
      // No sections in the payload, so the cart drawer and cart page re-render
      // themselves (which also clears the theme's "code not applicable" error
      // shown before the gift line existed).
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: { resource: fresh, sourceId: SOURCE, data: { source: SOURCE, itemCount: fresh.item_count } },
        })
      );
    }
  }

  // One run at a time; anything that arrives meanwhile triggers one more pass.
  function schedule() {
    if (running) {
      rerun = true;
      return;
    }
    running = true;
    (async () => {
      try {
        do {
          rerun = false;
          await reconcile();
        } while (rerun);
      } catch {
        // Never break the cart: network or parsing errors are ignored
      } finally {
        running = false;
      }
    })();
  }

  // Cart events the theme emits
  document.addEventListener('cart:update', (event) => {
    if (/** @type {CustomEvent} */ (event).detail?.data?.source === SOURCE) return;
    schedule();
  });
  document.addEventListener('discount:update', schedule);

  // Any /cart/add, /cart/change or /cart/update call, by the theme or an app.
  // Our own requests use nativeFetch, so they don't loop back here.
  window.fetch = function (input, options) {
    const request = nativeFetch(input, options);
    try {
      if (isCartMutationUrl(requestUrl(input))) request.then(schedule, () => {});
    } catch {
      // Ignore malformed requests
    }
    return request;
  };
  const nativeOpen = XMLHttpRequest.prototype.open;
  const nativeSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    /** @type {any} */ (this).__gwpCartMutation = isCartMutationUrl(String(url));
    return nativeOpen.call(this, method, url, ...rest);
  };
  XMLHttpRequest.prototype.send = function (...args) {
    if (/** @type {any} */ (this).__gwpCartMutation) this.addEventListener('loadend', schedule);
    return nativeSend.apply(this, args);
  };

  // Codes arriving via /discount/CODE links land in cart.discount_codes before
  // the page loads, so a check on load (and on back/forward cache restores) covers them.
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) schedule();
  });
  schedule();
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  init();
}
